import React, { useState, useEffect, createContext, useContext } from 'react';
import { AnonymousReviewData } from '../../types/anonymousReview';

const initialData: AnonymousReviewData = {
  files: [],
  title: '',
  description: '',
  creatorEmail: '',
  reviewerEmail: '',
  reviewerFirstName: '',
  reviewerLastName: '',
  deadlineEnabled: false,
  deadline: '',
  deadlineTime: '',
  reminderEnabled: false,
  reminders: [],
  customInstructions: '',
  timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  reviewLink: '',
  tempReviewId: null,
  anonymousReviewId: null,
};

// Context for sharing state across components
interface AnonymousReviewContextType {
  data: AnonymousReviewData;
  updateData: (updates: Partial<AnonymousReviewData>) => void;
  clearData: () => void;
  currentStep: number;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  error: string | null;
  setError: (error: string | null) => void;
  goToStep: (step: number) => void;
  nextStep: () => void;
  prevStep: () => void;
  validateCurrentStep: () => boolean;
  validateStep1: () => boolean;
  validateStep2: () => boolean;
}

const AnonymousReviewContext = createContext<AnonymousReviewContextType | null>(null);

export const AnonymousReviewProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [data, setData] = useState<AnonymousReviewData>(initialData);
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load data from localStorage on mount
  useEffect(() => {
    const savedData = localStorage.getItem('anonymousReviewData');
    console.log('Provider: Loading data from localStorage:', savedData);
    console.log('Provider: Saved data length:', savedData?.length);
    console.log('Provider: Saved data type:', typeof savedData);
    
    if (savedData) {
      try {
        const parsedData = JSON.parse(savedData);
        console.log('Provider: Parsed data from localStorage:', parsedData);
        
        // Reconstruct File object from base64 data if available
        let reconstructedFiles: File[] = [];
        if (parsedData.fileData && parsedData.fileMetadata && parsedData.fileMetadata.length > 0) {
          try {
            const fileMetadata = parsedData.fileMetadata[0];
            const base64Data = parsedData.fileData;
            
            console.log('Provider: Attempting to reconstruct file from base64 data');
            console.log('Provider: File metadata:', fileMetadata);
            console.log('Provider: Base64 data length:', base64Data.length);
            
            // Convert base64 to blob
            const byteCharacters = atob(base64Data.split(',')[1]);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: fileMetadata.type });
            
            // Create File object
            const file = new File([blob], fileMetadata.name, { 
              type: fileMetadata.type,
              lastModified: fileMetadata.lastModified 
            });
            
            reconstructedFiles = [file];
            console.log('Provider: Successfully reconstructed file from base64:', file);
            console.log('Provider: File name:', file.name);
            console.log('Provider: File size:', file.size);
            console.log('Provider: File type:', file.type);
          } catch (fileError) {
            console.error('Provider: Error reconstructing file:', fileError);
            console.error('Provider: Base64 data:', parsedData.fileData?.substring(0, 100) + '...');
          }
        } else {
          console.log('Provider: No file data found in localStorage');
          console.log('Provider: fileData exists:', !!parsedData.fileData);
          console.log('Provider: fileMetadata exists:', !!parsedData.fileMetadata);
        }
        
        setData(prev => {
          const newData = { ...prev, ...parsedData, files: reconstructedFiles };
          // Restore fileMetadata if available
          if (parsedData.fileMetadata) {
            newData.fileMetadata = parsedData.fileMetadata;
          }
          // Check if user came from landing page
          if (parsedData.hasFilesFromLandingPage) {
            newData.hasFilesFromLandingPage = true;
          }
          if (parsedData.preuploadedStoragePath) {
            (newData as any).preuploadedStoragePath = parsedData.preuploadedStoragePath;
            (newData as any).preuploadedFileName = parsedData.preuploadedFileName;
            (newData as any).preuploadedFileType = parsedData.preuploadedFileType;
          }
          console.log('Provider: Setting data from localStorage:', newData);
          return newData;
        });
      } catch (err) {
        console.error('Provider: Error loading anonymous review data:', err);
      }
    }
  }, []);

  // Save data to localStorage whenever it changes (excluding files but saving file metadata)
  useEffect(() => {
    try {
      const dataToSave = { ...data };
      // Save file metadata instead of actual File objects
      if (data.files && data.files.length > 0) {
        (dataToSave as any).fileMetadata = data.files.map(file => ({
          name: file.name,
          size: file.size,
          type: file.type,
          lastModified: file.lastModified
        }));
      }
      delete (dataToSave as any).files;
      console.log('Provider: Saving data to localStorage:', dataToSave);
      localStorage.setItem('anonymousReviewData', JSON.stringify(dataToSave));
    } catch (err) {
      console.error('Provider: Error saving anonymous review data:', err);
    }
  }, [data]);

  const updateData = (updates: Partial<AnonymousReviewData>) => {
    console.log('Provider: Updating anonymous review data:', updates);
    setData(prev => {
      const newData = { ...prev, ...updates };
      
      // Clear the landing page flag when files are actually uploaded
      if (updates.files && updates.files.length > 0) {
        newData.hasFilesFromLandingPage = false;
      }
      
      console.log('Provider: New data state:', newData);
      return newData;
    });
  };

  const clearData = () => {
    setData(initialData);
    localStorage.removeItem('anonymousReviewData');
  };

  const goToStep = (step: number) => {
    setCurrentStep(step);
    setError(null);
  };

  const nextStep = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
      setError(null);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setError(null);
    }
  };

  const validateStep1 = (): boolean => {
    if (!data.files || data.files.length === 0) {
      setError('Please select a file to upload');
      return false;
    }
    if (!data.title.trim()) {
      setError('Please enter a title for your review');
      return false;
    }
    return true;
  };

  const validateStep2 = (): boolean => {
    if (!data.creatorEmail.trim()) {
      setError('Please enter your email address');
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.creatorEmail)) {
      setError('Please enter a valid email address');
      return false;
    }
    if (!data.reviewerEmail.trim()) {
      setError('Please enter the reviewer email address');
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.reviewerEmail)) {
      setError('Please enter a valid reviewer email address');
      return false;
    }
    if (data.deadlineEnabled && (!data.deadline || !data.deadlineTime)) {
      setError('Please set both deadline date and time, or disable the deadline');
      return false;
    }
    return true;
  };

  const validateCurrentStep = (): boolean => {
    switch (currentStep) {
      case 1: return validateStep1();
      case 2: return validateStep2();
      default: return true;
    }
  };

  const value: AnonymousReviewContextType = {
    data,
    updateData,
    clearData,
    currentStep,
    isLoading,
    setIsLoading,
    error,
    setError,
    goToStep,
    nextStep,
    prevStep,
    validateCurrentStep,
    validateStep1,
    validateStep2,
  };

  return (
    <AnonymousReviewContext.Provider value={value}>
      {children}
    </AnonymousReviewContext.Provider>
  );
};

// Hook to use the context
export const useAnonymousReviewContext = () => {
  const context = useContext(AnonymousReviewContext);
  if (!context) {
    throw new Error('useAnonymousReviewContext must be used within AnonymousReviewProvider');
  }
  return context;
};